import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText, type ErrorText } from '../../api/errorText';
import type { LibraryKind, MediaSourceProvider } from '../../api/types';
import LibrarySourceFields from './LibrarySourceFields';
import { kindOf, locationsFor, sourceName } from './librarySources';

/// What a library of folders can be. Music and photos are not offered: the scanner would read
/// them as films with odd names.
const folderKinds: LibraryKind[] = ['Movies', 'Shows', 'Mixed', 'Other'];

/// Makes a library in two steps: where it reads from, then what it is called and what of that
/// source it takes. A library reads from exactly one source; the films and series pages show
/// every library together, so there is nothing to gain from mixing them.
export default function AddLibraryDialog({
  open,
  providers,
  onClose,
  onCreated,
}: {
  open: boolean;
  providers: MediaSourceProvider[];
  onClose: () => void;
  /// Called once the library exists, with why its first scan did not start where it did not.
  onCreated: (scanFailure?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [providerId, setProviderId] = useState<string>();
  const [name, setName] = useState('');
  const [folderKind, setFolderKind] = useState<LibraryKind>('Movies');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorText>();

  const provider = providers.find((candidate) => candidate.providerId === providerId);

  const reset = () => {
    setProviderId(undefined);
    setName('');
    setFolderKind('Movies');
    setAddresses([]);
    setError(undefined);
  };

  const close = () => {
    if (busy) {
      return;
    }

    reset();
    onClose();
  };

  const chosen = addresses.map((address) => address.trim()).filter((address) => address.length > 0);

  const kind = provider?.typedAddresses
    ? folderKind
    : kindOf(provider?.options.filter((option) => chosen.includes(option.address)) ?? []);

  const create = async () => {
    if (!provider || !kind) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      const created = await api.saveLibrary({
        id: crypto.randomUUID(),
        name: name.trim(),
        kind,
        locations: locationsFor(provider.providerId, chosen),

        // Descriptions and artwork are fetched in this language. Whoever is making the library is
        // reading it in theirs, which is the best guess there is.
        metadataLanguage: i18n.language,
        metadataProviders: [],
        lastScannedAt: null,
      });

      // A library nobody has read is an empty shelf with a name on it, which is not what
      // somebody who just picked a folder meant. The library exists either way: a scan that did
      // not start is reported to the page, not left in this dialog, where pressing create again
      // would make a second library.
      let scanFailure: string | undefined;

      try {
        await api.scanLibrary(created.id);
      } catch (caught) {
        scanFailure = apiErrorText(caught, t).message;
      }

      reset();
      onCreated(scanFailure);
    } catch (caught) {
      // The source checked the folder or the account and said what is wrong with it, in the
      // user's language: the code it returned is a key in that plugin's own catalogue.
      setError(apiErrorText(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const describe = (candidate: MediaSourceProvider) => {
    if (candidate.typedAddresses) {
      return t('libraries.add.folders');
    }

    if (candidate.optionsError) {
      return t('libraries.add.broken');
    }

    return candidate.options.length === 0
      ? t('libraries.add.notSetUp')
      : t('libraries.add.available', { count: candidate.options.length });
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{t('libraries.add.title')}</DialogTitle>

      <DialogContent>
        {!provider ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t('libraries.add.chooseSource')}
            </Typography>

            <List disablePadding>
              {providers.map((candidate) => (
                <ListItemButton
                  key={candidate.providerId}
                  onClick={() => {
                    setProviderId(candidate.providerId);
                    setAddresses([]);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText primary={sourceName(t, candidate)} secondary={describe(candidate)} />
                  {/* Points the way the next step is, which in a right-to-left language is left. */}
                  <ChevronRightIcon
                    color="action"
                    sx={{ transform: theme.direction === 'rtl' ? 'scaleX(-1)' : undefined }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error">
                {error.message}
                {error.remedy && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {error.remedy}
                  </Typography>
                )}
              </Alert>
            )}

            <Typography variant="subtitle2">
              {t('libraries.sourceNamed', { name: sourceName(t, provider) })}
            </Typography>

            <TextField
              autoFocus
              required
              label={t('library.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            {provider.typedAddresses && (
              <TextField
                select
                label={t('libraries.kind')}
                value={folderKind}
                onChange={(event) => setFolderKind(event.target.value as LibraryKind)}
                helperText={folderKind === 'Mixed' ? t('setup.library.kindMixedHint') : ' '}
              >
                {folderKinds.map((candidate) => (
                  <MenuItem key={candidate} value={candidate}>
                    {t(`libraryKind.${candidate}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <LibrarySourceFields provider={provider} addresses={addresses} onChange={setAddresses} />

            {!provider.typedAddresses && kind && (
              <Typography variant="body2" color="text.secondary">
                {t('libraries.kindNamed', { kind: t(`libraryKind.${kind}`) })}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {provider ? (
          <>
            <Button
              disabled={busy}
              onClick={() => {
                setProviderId(undefined);
                setError(undefined);
              }}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              disabled={busy || name.trim().length === 0 || chosen.length === 0 || !kind}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() => void create()}
            >
              {t('common.create')}
            </Button>
          </>
        ) : (
          <Button onClick={close}>{t('common.cancel')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
